import "../administration.css";

import { EXPERIMENT_STATUS, PLOTMAP_STATUS } from "../../constants";
import {
  createSite,
  deleteSite,
  fetchSites,
  fetchTrial,
  resetTrial,
  updateSiteAlert,
} from "../../actions/sites";
import { nextSort, sortRows } from "../sortable";

import { AdminTable } from "../AdminTable";
import { Button } from "reactstrap";
import CopyButton from "../../components/copyButton";
import { FilterBar } from "../../clientDashboard/components/FilterBar";
import { LevelHeader } from "../../clientDashboard/components/LevelHeader";
import PropTypes from "prop-types";
import React from "react";
import SiteModal from "./SiteModal";
import _ from "lodash";
import { connect } from "react-redux";
import { fetchCompanies } from "../../actions/companies";
import { fetchCompanyContracts } from "../../actions/company";
import { hasAdminAccess } from "../../services/roles";
import { requestFetchSite } from "../../services/backendRequests";

class AdministrationContract extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      siteSelected: null,
      missionSelected: null,
      search: "",
      countryFilter: [],
      cropFilter: [],
      sortKey: "name",
      sortDir: "asc",
    };
    this.createNewSite = this.createNewSite.bind(this);
  }

  handleSort = (column) => {
    this.setState((prev) => nextSort(prev, column));
  };

  // Unique {value,label} options for a site field, present-only.
  siteOptions(field) {
    const values = (this.props.sites.all || [])
      .map((site) => site[field])
      .filter(Boolean);
    return [...new Set(values)]
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }

  siteSortValue(site, key) {
    if (key === "nb_microplots") return site.nb_microplots ?? 0;
    return site[key] || "";
  }

  visibleSites() {
    const { search, countryFilter, cropFilter, sortKey, sortDir } = this.state;
    const needle = search.trim().toLowerCase();
    const filtered = (this.props.sites.all || []).filter((site) => {
      const matchesQuery =
        !needle ||
        site.name.toLowerCase().includes(needle) ||
        (site.display_name || "").toLowerCase().includes(needle);
      const matchesCountry =
        countryFilter.length === 0 || countryFilter.includes(site.country);
      const matchesCrop =
        cropFilter.length === 0 || cropFilter.includes(site.crop);
      return matchesQuery && matchesCountry && matchesCrop;
    });
    return sortRows(filtered, sortKey, sortDir, (s, k) =>
      this.siteSortValue(s, k)
    );
  }

  columns() {
    return [
      { key: "name", label: "Name", className: "w-25", sortable: true },
      { key: "display_name", label: "Display name", sortable: true },
      { key: "country", label: "Country", sortable: true },
      { key: "crop", label: "Crop", sortable: true },
      { key: "nb_microplots", label: "Microplots", sortable: true },
      {
        key: "actions",
        label: "Actions",
        cell: (site) => (
          <div className="admin-actions">
            <CopyButton value={site.id} datatip="Copy site UUID" />
            <Button
              data-tooltip-id="tooltip"
              data-tooltip-content="View upload sessions"
              color="link"
              onClick={(e) => {
                e.stopPropagation();
                this.props.history.push(
                  "/uploadsessions?company=" +
                    this.props.company.id +
                    "&contract=" +
                    this.props.contract.id +
                    "&site=" +
                    site.id
                );
              }}
            >
              <i className="fa-solid fa-lg fa-upload" />
            </Button>
            <Button
              data-tooltip-id="tooltip"
              data-tooltip-content="Delete site"
              data-tooltip-variant="error"
              data-tooltip-place="bottom"
              className="admin-actions__danger"
              color="link"
              onClick={(event) => {
                this.deleteSite(event, site);
              }}
            >
              <i className="fa-solid fa-lg fa-trash-can" />
            </Button>
          </div>
        ),
      },
    ];
  }

  renderFilters() {
    return (
      <FilterBar
        query={this.state.search}
        onQuery={(q) => this.setState({ search: q })}
        placeholder="Search a site…"
        selects={[
          {
            label: "Country",
            value: this.state.countryFilter,
            onChange: (vals) => this.setState({ countryFilter: vals }),
            allLabel: "All countries",
            options: this.siteOptions("country"),
          },
          {
            label: "Crop",
            value: this.state.cropFilter,
            onChange: (vals) => this.setState({ cropFilter: vals }),
            allLabel: "All crops",
            options: this.siteOptions("crop"),
          },
        ]}
        trailing={
          hasAdminAccess(this.props.user.self) && (
            <Button color="primary" size="sm" onClick={this.createNewSite}>
              <i className="fa-solid fa-plus me-1" /> New site
            </Button>
          )
        }
      />
    );
  }

  componentDidMount() {
    if (!this.props.company || !this.props.contract) {
      this.props.fetchCompanies();
      this.props.fetchCompanyContracts({ id: this.props.companyId });
      this.props.fetchSites({ id: this.props.contractId });
    } else {
      this.props.fetchSites(this.props.contract);
    }
  }

  openSiteEdition(site) {
    this.props.fetchTrial(site);
    requestFetchSite(site, this.props.user).then((siteSelected) => {
      siteSelected.missions = _.sortBy(siteSelected.missions, ["name"]);
      this.setState((state) => {
        return {
          ...state,
          missionSelected: null,
          siteSelected: {
            id: siteSelected.id,
            name: siteSelected.name,
            display_name: siteSelected.display_name,
            nb_microplots: siteSelected.nb_microplots,
            country: siteSelected.country,
            crop: siteSelected.crop,
            missions: siteSelected.missions,
            experiment_status: siteSelected.experiment_status,
            plotmap_status: siteSelected.plotmap_status,
          },
        };
      });
    });
  }

  createNewSite() {
    this.props.resetTrial();
    this.setState((state) => {
      return {
        ...state,
        missionSelected: null,
        siteSelected: {
          id: "",
          name: "Site " + (this.props.sites.all.length + 1),
          display_name: "Site " + (this.props.sites.all.length + 1),
          nb_microplots: 0,
          crop: "",
          country: null,
          missions: [],
          experiment_status: EXPERIMENT_STATUS.NOT_REQUIRED,
          plotmap_status: PLOTMAP_STATUS.NOT_DELIVERED,
        },
      };
    });
  }

  deleteSite(event, site) {
    event.stopPropagation();
    if (
      window.confirm("Do you really want to delete site: " + site.name + " ?")
    ) {
      this.props.deleteSite(site);
      this.setState({ siteSelected: null });
    }
  }

  handleCloseSiteModal() {
    this.props.updateSiteAlert(""); // Clear alerts when closing modal
    this.setState({
      siteSelected: null,
    });
  }

  render() {
    if (!this.props.company || !this.props.contract) return "";
    const sites = this.visibleSites();
    const { start_date, end_date } = this.props.contract;

    return (
      <div className="page-content container-fluid cf-page">
        <LevelHeader
          path={[
            { label: "Administration", to: "/administration" },
            {
              label: this.props.company.name,
              to: `/administration/company/${this.props.company.id}`,
            },
            { label: this.props.contract.name },
          ]}
          metrics={[
            { value: this.props.sites.all?.length ?? 0, label: "Sites" },
            { value: this.props.contract.users.length, label: "Users" },
            {
              value: `${start_date || "—"} → ${end_date || "—"}`,
              label: "Period",
            },
          ]}
          actions={
            <Button
              color="primary"
              outline
              className="text-nowrap"
              onClick={() =>
                this.props.history.push(
                  `/administration/company/${this.props.company.id}/contract/${this.props.contract.id}/users`
                )
              }
            >
              <i className="fa-solid fa-users me-1" aria-hidden="true" /> Manage
              users
            </Button>
          }
        />

        <SiteModal
          company={this.props.company}
          contract={this.props.contract}
          site={this.state.siteSelected}
          isOpen={
            this.state.siteSelected !== null || this.props.sites.alert !== ""
          }
          handleCloseModal={() => this.handleCloseSiteModal()}
        />

        {this.renderFilters()}
        <AdminTable
          columns={this.columns()}
          rows={sites}
          loading={this.props.refreshing}
          sortKey={this.state.sortKey}
          sortDir={this.state.sortDir}
          onSort={this.handleSort}
          rowKey={(site) => site.id + "_" + site.name}
          onRowClick={(site) => this.openSiteEdition(site)}
        />
      </div>
    );
  }
}

function mapStateToProps(store, props) {
  const { companyId, contractId } = props.match.params;
  const companies = store.companies.all;
  const contracts = store.company.contracts;
  let company = {};
  let contract = {};

  if (companies) company = companies.find((item) => item.id === companyId);

  if (contracts)
    contract = _.filter(store.company.contracts, { id: contractId })[0];

  return {
    user: store.user,
    company: company,
    companyId: companyId,
    contractId: contractId,
    contract: contract,
    sites: store.sites,
    refreshing: store.sites.table.refreshing,
  };
}

const callbacks = {
  fetchSites,
  fetchTrial,
  createSite,
  deleteSite,
  fetchCompanies,
  fetchCompanyContracts,
  updateSiteAlert,
  resetTrial,
};

export default connect(mapStateToProps, callbacks)(AdministrationContract);

AdministrationContract.propTypes = {
  fetchCompanies: PropTypes.func.isRequired,
  fetchCompanyContracts: PropTypes.func.isRequired,
  fetchSites: PropTypes.func.isRequired,
  fetchTrial: PropTypes.func.isRequired,
  resetTrial: PropTypes.func.isRequired,
  createSite: PropTypes.func.isRequired,
  updateSiteAlert: PropTypes.func.isRequired,
  refreshing: PropTypes.bool.isRequired,
};
